import type { TourSceneId } from '@belong/utils-house-scene/tour-scenes';
import { HOW_IT_WORKS_STRINGS as STRINGS } from 'src/modals/how-it-works/strings';

export const TOUR_AUDIENCES = [
  'HOMEOWNERS_PRO_SCREEN',
  'RESIDENTS_PRO_SCREEN',
] as const;

export type TourAudience = (typeof TOUR_AUDIENCES)[number];

export function isTourAudience(value: unknown): value is TourAudience {
  return TOUR_AUDIENCES.includes(value as TourAudience);
}

export type TourTarget = { audience: TourAudience; step: number };

export const tourTargetKey = (target: TourTarget) =>
  `${target.audience}:${target.step}`;

export const TOUR_SECTION_HEIGHT = 'h-[259px] md:h-[324px] lg:h-[454px]';

type TourStep = {
  label: string;
  copy: readonly (number | string)[];
  fallback?: number;
  scene: TourSceneId;
};

export const TOUR_STEPS: Record<TourAudience, readonly TourStep[]> = {
  HOMEOWNERS_PRO_SCREEN: [
    { label: STRINGS['tour.step.introduce'], copy: [0], scene: 'introduce' },
    { label: STRINGS['tour.step.analyze'], copy: [1], scene: 'analyze' },
    { label: STRINGS['tour.step.renovate'], copy: [2], scene: 'renovate' },
    { label: STRINGS['tour.step.style'], copy: [3], scene: 'style' },
    { label: STRINGS['tour.step.list'], copy: [5], scene: 'list' },
    { label: STRINGS['tour.step.tour'], copy: [4], scene: 'tour' },
    {
      label: STRINGS['tour.step.match'],
      copy: [
        STRINGS['tour.homeowners.match'],
        STRINGS['tour.homeowners.matchDetail'],
      ],
      fallback: 6,
      scene: 'match',
    },
    {
      label: STRINGS['tour.step.loved'],
      copy: [
        STRINGS['tour.homeowners.loved'],
        STRINGS['tour.homeowners.lovedDetail'],
      ],
      fallback: 9,
      scene: 'loved',
    },
    {
      label: STRINGS['tour.step.care'],
      copy: [
        STRINGS['tour.homeowners.care'],
        STRINGS['tour.homeowners.careDetail'],
      ],
      fallback: 10,
      scene: 'care',
    },
  ],
  RESIDENTS_PRO_SCREEN: [
    { label: STRINGS['tour.step.apply'], copy: [0], scene: 'apply' },
    { label: STRINGS['tour.step.moveIn'], copy: [1], scene: 'move-in' },
    {
      label: STRINGS['tour.step.pay'],
      copy: [
        STRINGS['tour.residents.pay'],
        STRINGS['tour.residents.payDetail'],
      ],
      fallback: 3,
      scene: 'pay',
    },
    { label: STRINGS['tour.step.meet'], copy: [2], scene: 'meet' },
    { label: STRINGS['tour.step.repair'], copy: [5], scene: 'renovate' },
  ],
};

export const tourScenes = (audience: TourAudience) =>
  TOUR_STEPS[audience].map((step) => step.scene);

export const tourFallbackSlide = ({ copy, fallback }: TourStep) =>
  fallback ?? copy.find((part): part is number => typeof part === 'number')!;

export const tourStepText = (
  slides: readonly { text: string }[],
  { copy }: TourStep,
) =>
  copy
    .map((part) =>
      (typeof part === 'string' ? part : slides[part]!.text)
        .replace(/\s*\n\s*/g, ' ')
        .trim(),
    )
    .join('\n');
